import { cn, deliveryMethodIcons } from "@au-schein-de/au-ui-library";
import {
	DELIVERY_METHOD_KIND,
	DELIVERY_METHOD_KINDS,
	type DeliveryMethodKind,
} from "@dransay/api/models";
import { useTranslation } from "react-i18next";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { FULFILLMENT_GROUP_KIND } from "~/features/checkout/delivery/fulfillment-groups.resolver";
import { FilterDropdownChip } from "~/features/products/pharmacy-picker/filter-dropdown-chip.component";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { deliveryMethodSelectedEvent } from "~/tooling/telemetry/events";

const HIDDEN_METHODS: ReadonlySet<DeliveryMethodKind> = new Set([
	DELIVERY_METHOD_KIND.EXPRESS_COURIER,
]);

function DeliveryMethodOptions({
	selected,
	onSelect,
}: Readonly<{
	selected: DeliveryMethodKind;
	onSelect: (method: DeliveryMethodKind) => void;
}>) {
	const { t } = useTranslation("common");
	const visibleMethods = DELIVERY_METHOD_KINDS.filter(
		(method) => !HIDDEN_METHODS.has(method),
	);
	return (
		<>
			{visibleMethods.map((method) => (
				<button
					key={method}
					type="button"
					className={cn(
						"flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-white p-3 text-body",
						method === selected
							? "border-2 border-ansay-500"
							: "border-2 border-transparent",
					)}
					data-testid={`pharmacy-picker-delivery-method-option-${method}`}
					onClick={() => onSelect(method)}
				>
					<span aria-hidden="true" className="[&_svg]:pointer-events-none">
						{deliveryMethodIcons[method]}
					</span>
					{t(`header.deliveryMethod.${method}`)}
				</button>
			))}
		</>
	);
}

/**
 * Mobile-only delivery-method switcher inside the picker's own filter row
 * (Figma 602). The desktop header switcher is always visible on desktop, so
 * this renders no desktop chip at all (hideDesktop) rather than duplicating
 * it — unlike Sort/Radius, which have no other desktop entry point.
 */
export function PharmacyPickerDeliveryMethodChip() {
	const { t } = useTranslation(["product", "common"]);
	const { selectedDeliveryMethodKind, setSelectedDeliveryMethodKind } =
		useVendorSelection();
	const method = selectedDeliveryMethodKind ?? "shipping";
	const label = t(`common:header.deliveryMethod.${method}`);

	const selectMethod = async (
		next: DeliveryMethodKind,
		onClose: () => void,
	) => {
		captureEvent(
			deliveryMethodSelectedEvent(next, FULFILLMENT_GROUP_KIND.MEDICATION),
		);
		try {
			await setSelectedDeliveryMethodKind(next);
		} finally {
			onClose();
		}
	};

	return (
		<FilterDropdownChip
			hideDesktop
			icon={
				<span
					aria-hidden="true"
					className="size-4 [&_svg]:size-4 [&_svg]:pointer-events-none [&_svg_*]:[stroke-width:1.6]"
				>
					{deliveryMethodIcons[method]}
				</span>
			}
			label={label}
			testId="pharmacy-picker-filter-delivery-method"
			panelWidthPx={228}
			sheetTitle={t("common:header.deliveryMethod.label")}
			sheetTestId="pharmacy-picker-delivery-method-sheet"
			renderMobileSheetContent={({ onClose }) => (
				<div className="flex flex-col gap-2">
					<DeliveryMethodOptions
						selected={method}
						onSelect={(next) => {
							void selectMethod(next, onClose);
						}}
					/>
				</div>
			)}
		/>
	);
}
